import { format } from "date-fns/format";
import { formatInTimeZone } from "date-fns-tz";
import { parseDate } from "./parseDate";
import { differenceInCalendarDays } from "date-fns";
import i18n from "i18n";

export const DATE_PATTERN_STANDARD = "LLL d, yyyy";
export const DATE_MONTH_YEAR = "LLL yyyy";
export const DATE_PATTERN_STANDARD_WEEKDAY = "EEE, LLLL d, yyyy";
export const DATE_PATTERN_TIME = "h:mmaaa";
export const DATE_RANGE = "dateRange";
export const DATE_PATTERN_MM_DD_YY = "MM.dd.yy";
/** Month/year only, e.g. 03/26 */
export const DATE_PATTERN_MM_YY = "MM/yy";
export const DATE_PATTERN_SIMPLE = "yyyy-MM-dd"; // ISO 8601 format

/** Group key for timeline views when createdDateISO is missing or invalid. */
export const DATE_GROUP_KEY_UNKNOWN = "unknown";

export function getDateGroupKey(
  date: string | Date | undefined | null,
  fallback = DATE_GROUP_KEY_UNKNOWN,
): string {
  if (!date) return fallback;
  try {
    const dateObj = typeof date === "string" ? parseDate(date) : date;
    if (Number.isNaN(dateObj.getTime())) return fallback;
    return format(dateObj, DATE_PATTERN_SIMPLE);
  } catch {
    return fallback;
  }
}

export function formatDate(
  date: string | Date | undefined,
  pattern = DATE_PATTERN_STANDARD,
) {
  if (!date) return "";
  const dateObj = typeof date === "string" ? parseDate(date) : date;
  if (pattern !== DATE_RANGE) {
    return format(dateObj, pattern, { weekStartsOn: 1 });
  }
  const dayDiff = differenceInCalendarDays(new Date(), dateObj);
  return dayDiff < 1
    ? i18n.t("core:DATE_RANGES.TODAY")
    : dayDiff < 7
      ? i18n.t("core:DATE_RANGES.THIS_WEEK")
      : dayDiff < 30
        ? i18n.t("core:DATE_RANGES.THIS_MONTH")
        : format(date, DATE_PATTERN_STANDARD, { weekStartsOn: 1 });
}

/**
 * Formats a membership renewal/paid-through date — `Membership.currentPeriodEnd`,
 * `manualRenewalDate`, `nextRenewalDate`, `renewalState.*` — which are pure
 * calendar days (no meaningful time-of-day) persisted as timestamps anchored
 * at *some* midnight depending on which backend writer produced them (UTC on
 * prod crons, America/Toronto for admin scripts — see `utcDayNumber` in the
 * backend's `renewalPeriod.service.ts`). Formatting with `formatDate`/`format`
 * renders in the *browser's* local timezone, which shifts the displayed day
 * back by one for any negative-UTC-offset viewer (all of North America) —
 * e.g. a stored `2027-06-15T00:00:00.000Z` shows as "Jun 14, 2027" in
 * America/Toronto. Reading the UTC calendar day instead is stable for either
 * writer convention (ET midnight always falls later within the same UTC day)
 * and matches the backend's own `formatRenewalEmailDate`. Use this — not
 * `formatDate` — for any of those fields.
 */
export function formatRenewalDate(
  date: string | Date | undefined | null,
  pattern = DATE_PATTERN_STANDARD,
): string {
  if (!date) return "";
  const dateObj = typeof date === "string" ? parseDate(date) : date;
  if (Number.isNaN(dateObj.getTime())) return "";
  return formatInTimeZone(dateObj, "UTC", pattern, { weekStartsOn: 1 });
}
